import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { ModelInstance } from '../../types/models';
import useSetState from '../../hooks/useSetState';
import * as API from '../../services/API';

import PopupDialog from '../shared/PopupDialog';

import SubscriptionsList from './ManageInterModelSubscriptions/SubscriptionsList';
import SubscriptionMapping from './ManageInterModelSubscriptions/SubscriptionMapping';
import ReplicateSubscriptions from './ManageInterModelSubscriptions/ReplicateSubscriptions';
import CopySubscriptionDialog from './ManageInterModelSubscriptions/CopySubscriptionDialog';
import ImdlRunHistoryDialog from './ManageInterModelSubscriptions/RunHistory/ImdlRunHistoryDialog';
import {
  IInaccessibleSource,
  downloadInaccessibleSourcesCsv,
} from './ManageInterModelSubscriptions/downloadInaccessibleSourcesCsv';

export interface ISubscription {
  id: number;
  scenarioLinkTagId: number;
  scenarioTag: string;
  status: 'Active' | 'Inactive';
}

export interface IDimMapping {
  dimensionName: string;
}

// A dimension mapped to a target Model Input (via InputDimensionMapping) or
// to a source Output's tag (via OutputDimensionMapping), carrying its own
// available attribute names (via ClientDimensionAttributeMapping). Fetched
// alongside the dimension list itself so the Dim Mapping Adjustments
// attribute dropdown never needs its own round trip.
export interface IDimensionOption {
  id: number;
  name: string;
  attributes: string[];
}

export interface IDimMappingAdjustment {
  sourceDimension: string;
  sourceDimAttribute: string;
  targetDimension: string;
  targetDimAttribute: string;
  mappingRule: string;
}

export interface IIMConnection {
  imConnectionId: string;
  imConnectionName: string;
  sourceModelId: number | '';
  sourceModelInstanceId: number | '';
  targetModelInputId: number | '';
  outputTagId: number | null;
  outputTagName: string | null;
  dimMappings: IDimMapping[];
  dimMappingAdjustments: IDimMappingAdjustment[];
  imEnabled: boolean;
}

export interface IModelOption {
  id: number;
  Name: string;
}

// A model instance that holds some scenario link tag as a Promotion
// (Submission) link - the only valid Source Model / Source Model Instance
// combinations for a connection built on that tag.
export interface ISourceModelInstanceOption {
  modelInstanceId: number;
  modelInstanceName: string;
  modelId: number;
  modelName: string;
}

interface IPromotedModelInstance extends ISourceModelInstanceOption {
  scenarioLinkTagId: number;
}

export interface IModelInputOption {
  id: number;
  name: string;
  outputTagId: number | null;
  outputTagName: string | null;
  dimensions: IDimensionOption[];
}

interface IManageInterModelSubscriptionsProps {
  isDialogOpen: boolean;
  handleCloseDialog: () => void;
  modelInstanceId?: ModelInstance['id'];
  instanceName?: string;
  clientId?: number;
  modelId?: number;
}

// Shapes of the raw API payloads, before they are mapped to the editor types.
interface IApiSubscriptionItem {
  id: number;
  scenarioLinkTagId: number;
  scenarioLinkTagName: string;
  status: 'Active' | 'Inactive';
}

interface IApiInputItem {
  id: number;
  name: string;
  outputTagId: number | null;
  outputTagName: string | null;
  dimensions?: IDimensionOption[];
}

interface IApiPromotedInstanceItem {
  scenario_link_tag_id: number;
  model_instance_id: number;
  model_instance_name: string;
  model_id: number;
  model_name: string;
}

interface IApiTargetInstance {
  id: number;
  Name: string;
}

export interface IApiDimMappingAdjustment {
  sourceDimension?: string;
  sourceAttribute?: string | null;
  targetDimension?: string;
  targetAttribute?: string | null;
  mappingRule?: IDimMappingAdjustment['mappingRule'];
}

export type IApiConnection = Pick<
  IIMConnection,
  | 'imConnectionId'
  | 'sourceModelId'
  | 'sourceModelInstanceId'
  | 'targetModelInputId'
> &
  Partial<
    Pick<
      IIMConnection,
      'outputTagId' | 'outputTagName' | 'dimMappings' | 'imEnabled'
    >
  > & {
    imConnectionName?: string;
    dimMappingAdjustments?: IApiDimMappingAdjustment[];
  };

interface IReplicateResult {
  created: number[];
  skipped: unknown[];
}

// Caught errors are unknown; the API client throws HTTPError (status + body),
// but only those fields are read, so a structural view is enough.
interface IApiErrorView {
  status?: number;
  body?: {
    message?: string;
    error?: string;
    lockedBy?: { displayName?: string };
    lockedAt?: string;
    items?: IInaccessibleSource[];
  };
}

const toApiError = (error: unknown): IApiErrorView =>
  typeof error === 'object' && error !== null ? (error as IApiErrorView) : {};

interface IManageInterModelSubscriptionsState {
  isDataLoading: boolean;
  isSaving: boolean;
  subscriptions: ISubscription[];
  activeView: 'list' | 'mapping' | 'replicate';
  selectedSubscriptionId: number | null;
  isRunHistoryOpen: boolean;
  isReadOnly: boolean;
  lockMessage: string | null;
  // True while SubscriptionMapping's own Add/Edit Connection card is open -
  // the outer dialog's Save/Cancel/X must all be disabled then, or the user
  // could save the subscription (or dismiss it) while a connection edit is
  // still in flight, silently dropping whatever they were mid-typing there.
  isConnectionEditorOpen: boolean;
  imConnections: IIMConnection[];
  subscriptionScenarioTagId: number | '';
  subscriptionStatus: 'Active' | 'Inactive';
  scenarioLinkTags: IModelOption[];
  models: IModelOption[];
  allPromotedInstances: IPromotedModelInstance[];
  availableModelInputs: IModelInputOption[];
  targetInstanceOptions: IModelOption[];
  selectedTargetInstanceId: number | '';
  replicateResult: IReplicateResult | null;
  copySubscription: ISubscription | null;
}

// The connection id is treated as a GUID by the API/worker. crypto.randomUUID
// is unavailable outside secure contexts (plain http, old browsers), so the
// fallback builds an RFC 4122 v4 GUID from the CSPRNG (getRandomValues).
export const generateConnectionId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // variant 10xx
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

// CLD-2910: identity of a connection for duplicate detection - every core field
// except the connection id, name and enabled flag. Adjustments are compared as a set, over
// the same saved shape the API keys on (blank rows dropped, "*" rows kept).
export const connectionDuplicateKey = (connection: IIMConnection): string =>
  JSON.stringify([
    connection.sourceModelId,
    connection.sourceModelInstanceId,
    connection.targetModelInputId,
    toApiAdjustments(connection.dimMappingAdjustments)
      .map((adj) =>
        JSON.stringify([
          adj.sourceDimension,
          adj.sourceAttribute || '',
          adj.targetDimension,
          adj.targetAttribute || '',
          adj.mappingRule,
        ])
      )
      .sort(),
  ]);

// CLD-2910: keeps the duplicate-connections toast readable however many or
// however long the connection names are.
const DUPLICATE_NAME_MAX_LENGTH = 30;
const DUPLICATE_MAX_NAMES_PER_SET = 3;
const DUPLICATE_MAX_SETS = 3;

const shortConnectionName = (name: string): string =>
  name.length > DUPLICATE_NAME_MAX_LENGTH
    ? `${name.slice(0, DUPLICATE_NAME_MAX_LENGTH - 1)}…`
    : name;

const describeDuplicateSets = (sets: IIMConnection[][]): string => {
  const described = sets.slice(0, DUPLICATE_MAX_SETS).map((set) => {
    const names = set
      .slice(0, DUPLICATE_MAX_NAMES_PER_SET)
      .map(
        (connection) => `"${shortConnectionName(connection.imConnectionName)}"`
      );
    const hidden = set.length - names.length;
    return hidden > 0
      ? `${names.join(', ')} +${hidden} more`
      : names.join(', ');
  });
  const hiddenSets = sets.length - described.length;
  return `${described.join('; ')}${
    hiddenSets > 0 ? `; and ${hiddenSets} more set(s)` : ''
  }`;
};
// "*" on a target dimension the source does not have (CLD-3055): every target
// row matching the other dimensions gets the value. Stored as this rule with
// only targetDimension; the editor shows it as the "*" target attribute.
export const ALL_MEMBERS_RULE = 'all_members';
export const ALL_MEMBERS_ATTRIBUTE = '*';
export const EXACT_MATCH_RULE = 'exact_match';

// The API and the worker read the rule case-insensitively and the API stores it
// as typed, so a rule saved as "ALL_MEMBERS" is still an all-members rule.
// Comparing it exactly would drop it on the next save, with no message.
const normalizeRule = (rule?: string): string =>
  (rule || '').trim().toLowerCase();

export const isAllMembersRule = (rule?: string): boolean =>
  normalizeRule(rule) === ALL_MEMBERS_RULE;

export const isAllMembersAdjustment = (adj: IDimMappingAdjustment): boolean =>
  isAllMembersRule(adj.mappingRule);

// The saved ConnectionsJson shape. Incomplete rows are dropped; a "*" rule has
// no source side, so it must not be dropped for lacking one - that would
// silently erase it on any save of the subscription (CLD-3055).
export const toApiAdjustments = (
  adjustments: IDimMappingAdjustment[]
): IApiDimMappingAdjustment[] =>
  adjustments
    .filter(
      (adj) =>
        adj.targetDimension &&
        (isAllMembersAdjustment(adj) || adj.sourceDimension)
    )
    .map((adj) =>
      isAllMembersAdjustment(adj)
        ? {
            targetDimension: adj.targetDimension,
            mappingRule: ALL_MEMBERS_RULE,
          }
        : {
            sourceDimension: adj.sourceDimension,
            sourceAttribute: adj.sourceDimAttribute || null,
            targetDimension: adj.targetDimension,
            targetAttribute: adj.targetDimAttribute || null,
            mappingRule: adj.mappingRule,
          }
    );

export const blankConnection = (): IIMConnection => ({
  imConnectionId: generateConnectionId(),
  imConnectionName: '',
  sourceModelId: '',
  sourceModelInstanceId: '',
  targetModelInputId: '',
  outputTagId: null,
  outputTagName: null,
  dimMappings: [],
  dimMappingAdjustments: [],
  imEnabled: true,
});

const initialState: IManageInterModelSubscriptionsState = {
  isDataLoading: false,
  isSaving: false,
  subscriptions: [],
  activeView: 'list',
  selectedSubscriptionId: null,
  isRunHistoryOpen: false,
  isReadOnly: false,
  lockMessage: null,
  isConnectionEditorOpen: false,
  imConnections: [],
  subscriptionScenarioTagId: '',
  subscriptionStatus: 'Active',
  scenarioLinkTags: [],
  models: [],
  allPromotedInstances: [],
  availableModelInputs: [],
  targetInstanceOptions: [],
  selectedTargetInstanceId: '',
  replicateResult: null,
  copySubscription: null,
};

// The API surfaces some failures as a bare machine-readable code rather
// than prose (e.g. the 409 on a duplicate scenario_link_tag_id) - mapped
// here to something a user can act on. Anything not in this list is
// assumed to already be a readable, server-written message.
// API edit lock expires after 30 minutes; refresh well inside that window.
const LOCK_REFRESH_MS = 10 * 60 * 1000;

const SAVE_ERROR_MESSAGES: Record<string, string> = {
  SUBSCRIPTION_EXISTS:
    'A subscription already exists for this Subscription Scenario Tag',
};

const readableSaveErrorMessage = (
  rawMessage: string | undefined
): string | undefined =>
  (rawMessage && SAVE_ERROR_MESSAGES[rawMessage]) || rawMessage;

export const fromApiConnection = (raw: IApiConnection): IIMConnection => ({
  imConnectionId: raw.imConnectionId,
  imConnectionName: raw.imConnectionName || '',
  sourceModelId: raw.sourceModelId,
  sourceModelInstanceId: raw.sourceModelInstanceId,
  targetModelInputId: raw.targetModelInputId,
  outputTagId: raw.outputTagId ?? null,
  // ConnectionsJson is stored exactly as last saved and never carries an
  // outputTagName (it's a display label, not data the backend derives) -
  // resolved instead from availableModelInputs once both have loaded, in
  // the component below, keyed on targetModelInputId.
  outputTagName: raw.outputTagName ?? null,
  dimMappings: raw.dimMappings || [],
  // Saved/returned as {sourceDimension, sourceAttribute, targetDimension,
  // targetAttribute, mappingRule} (see handleSaveSubscription) - renamed
  // back to the editor's sourceDimAttribute/targetDimAttribute fields here.
  dimMappingAdjustments: (raw.dimMappingAdjustments || []).map((adj) => ({
    sourceDimension: adj.sourceDimension || '',
    sourceDimAttribute: adj.sourceAttribute || '',
    targetDimension: adj.targetDimension || '',
    targetDimAttribute: isAllMembersRule(adj.mappingRule)
      ? ALL_MEMBERS_ATTRIBUTE
      : adj.targetAttribute || '',
    // Canonical for "*", so every later comparison sees the same value.
    mappingRule: isAllMembersRule(adj.mappingRule)
      ? ALL_MEMBERS_RULE
      : adj.mappingRule || EXACT_MATCH_RULE,
  })),
  imEnabled: !!raw.imEnabled,
});

const ManageInterModelSubscriptions: FunctionComponent<
  IManageInterModelSubscriptionsProps
> = ({
  isDialogOpen,
  handleCloseDialog,
  modelInstanceId,
  instanceName = 'Instance',
  clientId,
  modelId,
}) => {
  const [state, setState] =
    useSetState<IManageInterModelSubscriptionsState>(initialState);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const heldLockIdRef = useRef<number | null>(null);
  const releaseLockRef = useRef<() => void>(() => undefined);
  // Kept in memory only (never logged or persisted): beforeunload cannot await
  // token acquisition, so the token is resolved when the lock is taken.
  const lockTokenRef = useRef<string | null>(null);
  const isLockHeld = !!state.selectedSubscriptionId && !state.isReadOnly;

  // Release on unmount (dialog closed by other means, navigation).
  useEffect(
    () => () => {
      releaseLockRef.current();
    },
    []
  );

  // Release on tab close/refresh. keepalive lets the request outlive the page.
  useEffect(() => {
    if (!isLockHeld) return undefined;
    const handleBeforeUnload = (): void => {
      const lockedId = heldLockIdRef.current;
      const token = lockTokenRef.current;
      if (lockedId === null || !token) return; // fail closed; lock auto-expires
      heldLockIdRef.current = null;
      fetch(`${API.getApiBase()}/imdl_subscriptions/${lockedId}/lock`, {
        method: 'DELETE',
        keepalive: true,
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {
        // Best-effort — the lock also auto-expires after 30 minutes.
      });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isLockHeld]);

  // The API lock expires after 30 minutes without a refresh; a repeated
  // edit-intent GET by the holder refreshes it. The response body is ignored so
  // in-progress edits are never overwritten.
  useEffect(() => {
    if (!isLockHeld) return undefined;
    const lockedId = heldLockIdRef.current;
    if (lockedId === null) return undefined;
    let stopped = false;
    const refresh = (): void => {
      if (heldLockIdRef.current !== lockedId) {
        clearInterval(timer);
        return;
      }
      API.load(`/imdl_subscriptions/${lockedId}?edit_intent=true`).catch(() => {
        if (stopped || heldLockIdRef.current !== lockedId) return;
        clearInterval(timer);
        enqueueSnackbar(
          'Your edit lock could not be refreshed; another user may be able to edit this subscription.',
          { variant: 'warning' }
        );
      });
    };
    const timer = setInterval(refresh, LOCK_REFRESH_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [isLockHeld, state.selectedSubscriptionId, enqueueSnackbar]);

  const modelName = state.models.find((m) => m.id === modelId)?.Name || 'Model';

  const loadSubscriptions = useCallback(async (): Promise<void> => {
    if (!modelInstanceId) return;
    setState({ isDataLoading: true });
    try {
      const response = await API.load(
        `/instances/${modelInstanceId}/imdl_subscriptions`
      );
      const subscriptions: ISubscription[] = (response?.items || []).map(
        (item: IApiSubscriptionItem) => ({
          id: item.id,
          scenarioLinkTagId: item.scenarioLinkTagId,
          scenarioTag: item.scenarioLinkTagName,
          status: item.status,
        })
      );
      setState({ subscriptions, isDataLoading: false });
    } catch (error: unknown) {
      setState({ subscriptions: [], isDataLoading: false });
      enqueueSnackbar(
        toApiError(error).body?.message || 'Failed to load subscriptions',
        { variant: 'error' }
      );
    }
  }, [modelInstanceId, setState, enqueueSnackbar]);

  // Reloaded when a subscription is opened: Submission Tags may change while the dialog is open.
  const loadPromotedInstances = useCallback(
    async (resetOnError: boolean): Promise<void> => {
      if (!clientId) return;
      try {
        const response = await API.load(
          `/clients/${clientId}/scenario_link_tags/promoted_instances`
        );
        setState({
          allPromotedInstances: (response?.items || []).map(
            (item: IApiPromotedInstanceItem) => ({
              scenarioLinkTagId: item.scenario_link_tag_id,
              modelInstanceId: item.model_instance_id,
              modelInstanceName: item.model_instance_name,
              modelId: item.model_id,
              modelName: item.model_name,
            })
          ),
        });
      } catch {
        if (resetOnError) setState({ allPromotedInstances: [] });
      }
    },
    [clientId, setState]
  );

  useEffect(() => {
    if (!(isDialogOpen && modelInstanceId && clientId)) return;

    loadSubscriptions();

    // Scoped to tags mapped as a Subscription Tag on this (target) instance,
    // rather than every tag in the client.
    API.load(
      `/clients/${clientId}/scenario_link_tags/instance_subscription_tags?model_instance_id=${modelInstanceId}`
    )
      .then((response) => setState({ scenarioLinkTags: response?.items || [] }))
      .catch(() => setState({ scenarioLinkTags: [] }));

    API.load(`/clients/${clientId}/models`)
      .then((response) => setState({ models: response || [] }))
      .catch(() => setState({ models: [] }));

    API.load(`/instances/${modelInstanceId}/imdl_inputs`)
      .then((response) =>
        setState({
          availableModelInputs: (response?.items || []).map(
            (item: IApiInputItem) => ({
              id: item.id,
              name: item.name,
              outputTagId: item.outputTagId,
              outputTagName: item.outputTagName,
              dimensions: item.dimensions || [],
            })
          ),
        })
      )
      .catch(() => setState({ availableModelInputs: [] }));

    loadPromotedInstances(true);

    if (modelId) {
      API.load(`/clients/${clientId}/models/${modelId}/model_instances`)
        .then((response) =>
          setState({
            targetInstanceOptions: (response || [])
              .filter((mi: IApiTargetInstance) => mi.id !== modelInstanceId)
              .map((mi: IApiTargetInstance) => ({ id: mi.id, Name: mi.Name })),
          })
        )
        .catch(() => setState({ targetInstanceOptions: [] }));
    }
  }, [
    isDialogOpen,
    modelInstanceId,
    clientId,
    modelId,
    loadSubscriptions,
    loadPromotedInstances,
    setState,
  ]);

  const sourceModelInstances: ISourceModelInstanceOption[] =
    state.allPromotedInstances.filter(
      (instance) =>
        instance.scenarioLinkTagId === state.subscriptionScenarioTagId &&
        // Can't subscribe to another instance within its own model.
        instance.modelId !== modelId
    );

  const sourceModelOptions: IModelOption[] = Array.from(
    new Map(
      sourceModelInstances.map((instance) => [
        instance.modelId,
        { id: instance.modelId, Name: instance.modelName },
      ])
    ).values()
  );

  const handleSubscriptionScenarioTagChange = (value: number | '') => {
    setState({
      subscriptionScenarioTagId: value,
      // Previously-picked Source Model/Instance may not hold the new tag.
      imConnections: state.imConnections.map((connection) => ({
        ...connection,
        sourceModelId: '',
        sourceModelInstanceId: '',
      })),
    });
  };

  // Fire-and-forget by design: releasing the lock is best-effort (it also
  // auto-expires after 30 minutes), so Cancel/Close/Back must never make
  // the user wait on this network call before the dialog actually closes -
  // only Save has real work worth waiting for.
  // heldLockIdRef is set only when edit_intent succeeded (never for the
  // read-only views) and cleared on release, so a release is sent at most once
  // across explicit close, unmount and beforeunload.
  const releaseLockIfHeld = (): void => {
    const lockedId = heldLockIdRef.current;
    if (lockedId === null) return;
    heldLockIdRef.current = null;
    API.remove(`/imdl_subscriptions/${lockedId}/lock`).catch(() => {
      // Best-effort — the lock also auto-expires after 30 minutes.
    });
  };
  releaseLockRef.current = releaseLockIfHeld;

  const handleReplicateSubscriptions = () => {
    setState({
      activeView: 'replicate',
      selectedTargetInstanceId: '',
      replicateResult: null,
    });
  };

  const handleEditSubscription = async (
    subscription: ISubscription
  ): Promise<void> => {
    setState({ isDataLoading: true });
    try {
      const [response] = await Promise.all([
        API.load(`/imdl_subscriptions/${subscription.id}?edit_intent=true`),
        loadPromotedInstances(false),
      ]);

      heldLockIdRef.current = response.id;
      API.resolveBearerToken()
        .then((token) => {
          lockTokenRef.current = token ?? null;
        })
        .catch(() => {
          lockTokenRef.current = null;
        });

      setState({
        isDataLoading: false,
        activeView: 'mapping',
        isReadOnly: false,
        lockMessage: null,
        isConnectionEditorOpen: false,
        selectedSubscriptionId: response.id,
        subscriptionScenarioTagId: response.scenarioLinkTagId,
        subscriptionStatus: response.status,
        imConnections: (response.connections || []).map(fromApiConnection),
      });
    } catch (error: unknown) {
      const apiError = toApiError(error);
      // The API signals another user's edit lock as HTTP 423 (HTTPError), not
      // a 200 body, so the read-only view is opened from the error path.
      if (apiError.status === 423 && apiError.body?.error === 'LOCKED') {
        const { lockedBy, lockedAt } = apiError.body;
        setState({
          isDataLoading: false,
          activeView: 'mapping',
          isReadOnly: true,
          isConnectionEditorOpen: false,
          lockMessage: `Locked for editing by ${
            lockedBy?.displayName || 'another user'
          } at ${
            lockedAt ? new Date(lockedAt).toLocaleString() : 'an earlier time'
          }.`,
          selectedSubscriptionId: subscription.id,
          subscriptionScenarioTagId: subscription.scenarioLinkTagId,
          subscriptionStatus: subscription.status,
          imConnections: [],
        });
        return;
      }
      // edit_intent is refused with 403 when the user may view but not edit;
      // open the same read-only view (empty connections, like the 423 path).
      if (apiError.status === 403) {
        setState({
          isDataLoading: false,
          activeView: 'mapping',
          isReadOnly: true,
          isConnectionEditorOpen: false,
          lockMessage: 'You have view-only access to this subscription.',
          selectedSubscriptionId: subscription.id,
          subscriptionScenarioTagId: subscription.scenarioLinkTagId,
          subscriptionStatus: subscription.status,
          imConnections: [],
        });
        return;
      }
      setState({ isDataLoading: false });
      enqueueSnackbar(
        apiError.body?.message || 'Failed to open subscription for editing',
        { variant: 'error' }
      );
    }
  };

  const handleNewSubscription = () => {
    setState({
      activeView: 'mapping',
      selectedSubscriptionId: null,
      isReadOnly: false,
      lockMessage: null,
      isConnectionEditorOpen: false,
      subscriptionScenarioTagId: '',
      subscriptionStatus: 'Active',
      // Starts empty - the connections table's own Add Connection flow is
      // what creates the first (and every later) connection now.
      imConnections: [],
    });
  };

  const handleDeleteSubscription = async (
    subscription: ISubscription
  ): Promise<void> => {
    try {
      await API.remove(`/imdl_subscriptions/${subscription.id}`);
      enqueueSnackbar('Subscription deleted', { variant: 'success' });
      loadSubscriptions();
    } catch (error: unknown) {
      enqueueSnackbar(
        toApiError(error).body?.message || 'Failed to delete subscription',
        { variant: 'error' }
      );
    }
  };

  const handleBackToList = (): void => {
    releaseLockIfHeld();
    setState({
      activeView: 'list',
      selectedSubscriptionId: null,
      isReadOnly: false,
      lockMessage: null,
      isConnectionEditorOpen: false,
      subscriptionScenarioTagId: '',
      subscriptionStatus: 'Active',
      imConnections: [],
      selectedTargetInstanceId: '',
      replicateResult: null,
    });
    loadSubscriptions();
  };

  const handleReplicateConfirm = async (): Promise<void> => {
    if (!state.selectedTargetInstanceId || !modelInstanceId) return;
    setState({ isSaving: true });
    try {
      const result = await API.create<IReplicateResult>(
        `/instances/${modelInstanceId}/imdl_subscriptions/replicate`,
        { target_model_instance_id: state.selectedTargetInstanceId }
      );
      setState({ isSaving: false, replicateResult: result });
      const skippedCount = result?.skipped?.length || 0;
      const createdCount = result?.created?.length || 0;
      enqueueSnackbar(
        `Replicated ${createdCount} subscription(s)${
          skippedCount ? `, ${skippedCount} skipped` : ''
        }`,
        { variant: skippedCount ? 'warning' : 'success' }
      );
    } catch (error: unknown) {
      setState({ isSaving: false });
      enqueueSnackbar(
        toApiError(error).body?.message || 'Failed to replicate subscriptions',
        { variant: 'error' }
      );
    }
  };

  const handleSaveSubscription = async (): Promise<void> => {
    if (!modelInstanceId) return;
    if (!state.subscriptionScenarioTagId) {
      enqueueSnackbar('Subscription Scenario Tag is required', {
        variant: 'error',
      });
      return;
    }
    if (state.imConnections.length === 0) {
      enqueueSnackbar('At least one IM Connection is required', {
        variant: 'error',
      });
      return;
    }
    for (const connection of state.imConnections) {
      if (!connection.imConnectionName.trim()) {
        enqueueSnackbar('Every connection needs a Connection Name', {
          variant: 'error',
        });
        return;
      }
      if (
        !connection.sourceModelId ||
        !connection.sourceModelInstanceId ||
        !connection.targetModelInputId
      ) {
        enqueueSnackbar(
          'Every connection needs a Source Model, Source Model Instance, and Model Input',
          { variant: 'error' }
        );
        return;
      }
    }

    // CLD-2910: no two connections may be identical apart from id, name and enabled flag.
    const connectionsByKey = new Map<string, IIMConnection[]>();
    state.imConnections.forEach((connection) => {
      const key = connectionDuplicateKey(connection);
      connectionsByKey.set(key, [
        ...(connectionsByKey.get(key) || []),
        connection,
      ]);
    });
    const duplicateSets = Array.from(connectionsByKey.values()).filter(
      (set) => set.length > 1
    );
    if (duplicateSets.length > 0) {
      enqueueSnackbar(
        `Identical connections found: ${describeDuplicateSets(
          duplicateSets
        )}. Keep only one of each set before saving.`,
        { variant: 'error' }
      );
      return;
    }

    const payload = {
      scenario_link_tag_id: state.subscriptionScenarioTagId,
      status: state.subscriptionStatus,
      connections: state.imConnections.map((connection) => {
        // Dim Mappings are read-only in the UI, mirroring the dimensions
        // actually mapped to the chosen target Model Input rather than
        // user-typed names.
        const targetInput = state.availableModelInputs.find(
          (input) => input.id === connection.targetModelInputId
        );
        return {
          imConnectionId: connection.imConnectionId,
          imConnectionName: connection.imConnectionName,
          sourceModelId: connection.sourceModelId,
          sourceModelInstanceId: connection.sourceModelInstanceId,
          targetModelInputId: connection.targetModelInputId,
          outputTagId: connection.outputTagId,
          dimMappings: (targetInput?.dimensions || []).map((dim) => ({
            dimensionId: dim.id,
            dimensionName: dim.name,
          })),
          dimMappingAdjustments: toApiAdjustments(
            connection.dimMappingAdjustments
          ),
          selectionCriteria: null,
          imEnabled: connection.imEnabled,
        };
      }),
    };

    setState({ isSaving: true });
    try {
      if (state.selectedSubscriptionId) {
        await API.put(
          `/imdl_subscriptions/${state.selectedSubscriptionId}`,
          payload
        );
      } else {
        await API.create(
          `/instances/${modelInstanceId}/imdl_subscriptions`,
          payload
        );
      }
      setState({ isSaving: false });
      enqueueSnackbar('Subscription saved successfully', {
        variant: 'success',
      });
      handleBackToList();
    } catch (error: unknown) {
      setState({ isSaving: false });
      const { body } = toApiError(error);
      // CLD-2910: several inaccessible sources are offered as a CSV, not listed in the toast.
      if (body?.error === 'SOURCES_NOT_ACCESSIBLE' && body.items) {
        const sources = body.items;
        enqueueSnackbar(body.message, {
          variant: 'error',
          persist: true,
          action: (snackbarId) => (
            <>
              <Button
                size="small"
                sx={{ color: 'common.white' }}
                onClick={() => downloadInaccessibleSourcesCsv(sources)}
              >
                Download CSV
              </Button>
              <IconButton
                size="small"
                sx={{ color: 'common.white' }}
                onClick={() => closeSnackbar(snackbarId)}
              >
                <Close fontSize="small" />
              </IconButton>
            </>
          ),
        });
        return;
      }
      enqueueSnackbar(
        readableSaveErrorMessage(body?.message) ||
          'Failed to save subscription',
        { variant: 'error' }
      );
    }
  };

  const handleAddConnection = (connection: IIMConnection) => {
    setState({
      imConnections: [...state.imConnections, connection],
    });
  };

  const handleRemoveConnection = (imConnectionId: string) => {
    setState({
      imConnections: state.imConnections.filter(
        (connection) => connection.imConnectionId !== imConnectionId
      ),
    });
  };

  const handleConnectionEditorOpenChange = (isOpen: boolean) => {
    setState({ isConnectionEditorOpen: isOpen });
  };

  const handleConnectionChange = (
    imConnectionId: string,
    updates: Partial<IIMConnection>
  ) => {
    setState({
      imConnections: state.imConnections.map((connection) =>
        connection.imConnectionId === imConnectionId
          ? { ...connection, ...updates }
          : connection
      ),
    });
  };

  const handleClose = (): void => {
    releaseLockIfHeld();
    setState(initialState);
    handleCloseDialog();
  };

  return (
    <PopupDialog
      open={isDialogOpen}
      title={
        state.activeView === 'list'
          ? 'Inter-Model Subscriptions'
          : state.activeView === 'mapping'
            ? 'Inter-Model Subscription Mapping'
            : 'Inter-Model Subscriptions Copy to Model Instance'
      }
      close={handleClose}
      maxWidth="lg"
      fullWidth
      isLoading={state.isSaving}
      // Blocks the title-bar X, backdrop click, and Escape - while a
      // connection's Add/Edit card is open, the only way out should be
      // that card's own Save/Cancel, never the dialog chrome around it.
      // Deliberately kept separate from `isLoading`: that prop also puts
      // every primary button into LoadingButton's spinner state, which
      // would make the dialog look permanently stuck "saving" for as long
      // as the user is simply working on a connection.
      closeDisabled={state.isConnectionEditorOpen}
      // The 'mapping' and 'replicate' views supply their own Cancel/Back
      // button in primaryButtons - PopupDialog's built-in one must be
      // suppressed there or it doubles up. The 'list' view has no explicit
      // button of its own, so it keeps the built-in one.
      hideCloseButton={state.activeView !== 'list'}
      primaryButtons={
        state.activeView === 'mapping'
          ? state.isReadOnly
            ? [
                {
                  id: 'back-subscription',
                  label: 'Back',
                  onClick: handleBackToList,
                },
              ]
            : [
                {
                  id: 'save-subscription',
                  label: state.isSaving ? 'Saving...' : 'Save',
                  onClick: handleSaveSubscription,
                  disabled: state.isSaving || state.isConnectionEditorOpen,
                },
                {
                  id: 'cancel-subscription',
                  label: 'Cancel',
                  onClick: handleBackToList,
                  // Disabled (not loading) while saving - `loading` would
                  // otherwise fall back to the dialog's shared `isLoading`
                  // (true during the save), spinning a button that isn't
                  // actually doing anything.
                  loading: false,
                  disabled: state.isSaving || state.isConnectionEditorOpen,
                },
              ]
          : state.activeView === 'replicate'
            ? [
                {
                  id: 'replicate-confirm',
                  label: state.isSaving
                    ? 'Replicating...'
                    : 'Replicate Subscriptions',
                  onClick: handleReplicateConfirm,
                  disabled: state.isSaving || !state.selectedTargetInstanceId,
                },
                {
                  id: 'replicate-cancel',
                  label: 'Cancel',
                  onClick: handleBackToList,
                  // Matches cancel-subscription: avoid handleBackToList
                  // navigating away mid-request, which would let the
                  // in-flight replicate call's .then/.catch fire a
                  // snackbar and setState after the user has moved on.
                  loading: false,
                  disabled: state.isSaving,
                },
              ]
            : []
      }
    >
      <Box>
        {state.activeView === 'list' && (
          <SubscriptionsList
            modelName={modelName}
            instanceName={instanceName}
            subscriptions={state.subscriptions}
            isDataLoading={state.isDataLoading}
            onReplicateSubscriptions={handleReplicateSubscriptions}
            onEditSubscription={handleEditSubscription}
            onCopySubscription={(subscription) =>
              setState({ copySubscription: subscription })
            }
            onDeleteSubscription={handleDeleteSubscription}
            onNewSubscription={handleNewSubscription}
            onViewHistory={() => setState({ isRunHistoryOpen: true })}
          />
        )}

        {state.activeView === 'mapping' && (
          <SubscriptionMapping
            modelName={modelName}
            instanceName={instanceName}
            isReadOnly={state.isReadOnly}
            lockMessage={state.lockMessage}
            isExistingSubscription={!!state.selectedSubscriptionId}
            subscriptionScenarioTagId={state.subscriptionScenarioTagId}
            subscriptionStatus={state.subscriptionStatus}
            scenarioLinkTags={state.scenarioLinkTags}
            imConnections={state.imConnections}
            availableModelInputs={state.availableModelInputs}
            models={sourceModelOptions}
            sourceModelInstances={sourceModelInstances}
            onSubscriptionScenarioTagChange={
              handleSubscriptionScenarioTagChange
            }
            onSubscriptionStatusChange={(value) =>
              setState({ subscriptionStatus: value as ISubscription['status'] })
            }
            onConnectionChange={handleConnectionChange}
            onAddConnection={handleAddConnection}
            onRemoveConnection={handleRemoveConnection}
            onConnectionEditorOpenChange={handleConnectionEditorOpenChange}
            onBackToList={handleBackToList}
            isSaving={state.isSaving}
          />
        )}

        {state.activeView === 'replicate' && (
          <ReplicateSubscriptions
            modelName={modelName}
            instanceName={instanceName}
            subscriptionsToReplicate={state.subscriptions}
            targetInstanceOptions={state.targetInstanceOptions}
            selectedTargetInstance={state.selectedTargetInstanceId}
            replicateResult={state.replicateResult}
            onSelectedTargetInstanceChange={(value) =>
              setState({ selectedTargetInstanceId: value })
            }
            onBackToList={handleBackToList}
          />
        )}

        <CopySubscriptionDialog
          subscription={state.copySubscription}
          clientId={clientId}
          modelInstanceId={modelInstanceId}
          onClose={() => setState({ copySubscription: null })}
          onFinished={() => {
            setState({ copySubscription: null });
            loadSubscriptions();
          }}
        />
        <ImdlRunHistoryDialog
          open={state.isRunHistoryOpen}
          close={() => setState({ isRunHistoryOpen: false })}
          modelName={modelName}
          instanceName={instanceName}
          modelInstanceId={modelInstanceId}
        />
      </Box>
    </PopupDialog>
  );
};

export default ManageInterModelSubscriptions;
